import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import './UploadModal.css';
import { API_BASE } from '../config/api';

function UploadModal({ onClose, onUpload, user, asPage = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [description, setDescription] = useState('');
  const [bookFile, setBookFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [visibility, setVisibility] = useState('public');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const [forSale, setForSale] = useState(false);
  const [priceAmount, setPriceAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [salesEligibility, setSalesEligibility] = useState({
    loading: false,
    eligible: false,
    reason: '',
    member_since: null,
    bypass_price_usd: 5
  });

  const PLATFORM_FEE_RATE = 0.01;

  const allCurrencies = useMemo(() => {
    try {
      if (typeof Intl.supportedValuesOf === 'function') {
        const codes = Intl.supportedValuesOf('currency');
        if (!codes.includes('CDF')) codes.push('CDF');
        return [...new Set(codes)].sort();
      }
    } catch (err) {
      console.warn('Intl.supportedValuesOf indisponible:', err);
    }

    return [
      'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR', 'BRL', 'ZAR',
      'NGN', 'KES', 'XAF', 'XOF', 'GHS', 'TND', 'MAD', 'RWF', 'UGX', 'CDF'
    ];
  }, []);

  const availablePaymentMethods = useMemo(() => ([
    'Orange Money'
  ]), []);

  useEffect(() => {
    const fetchSalesEligibility = async () => {
      if (!user?.id) return;
      try {
        setSalesEligibility((prev) => ({ ...prev, loading: true }));
        const res = await axios.get(`${API_BASE}/api/users/${user.id}/sales-eligibility`);
        const eligible = !!res.data?.eligible;
        setSalesEligibility({
          loading: false,
          eligible,
          reason: res.data?.reason || '',
          member_since: res.data?.member_since || null,
          bypass_price_usd: Number(res.data?.bypass_price_usd || 5)
        });

        const params = new URLSearchParams(location.search || '');
        if (params.get('seller_paid') === '1' && eligible) {
          setForSale(true);
        }
      } catch (err) {
        setSalesEligibility({
          loading: false,
          eligible: false,
          reason: "Impossible de verifier l'eligibilite de vente.",
          member_since: null,
          bypass_price_usd: 5
        });
      }
    };

    fetchSalesEligibility();
  }, [location.search, user?.id]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleTogglePaymentMethod = (method) => {
    setPaymentMethods((prev) => (
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    ));
  };

  useEffect(() => {
    if (forSale) {
      setPaymentMethods(['Orange Money']);
    } else {
      setPaymentMethods([]);
    }
  }, [forSale]);

  const parsedPriceAmount = Number(priceAmount);
  const estimatedFee = Number.isFinite(parsedPriceAmount) ? parsedPriceAmount * PLATFORM_FEE_RATE : 0;
  const estimatedSellerAmount = Number.isFinite(parsedPriceAmount) ? parsedPriceAmount - estimatedFee : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !author || !bookFile) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (!user) {
      alert('Vous devez etre connecte pour publier un livre.');
      return;
    }

    if (forSale) {
      if (!salesEligibility.eligible) {
        alert(salesEligibility.reason || "Vous devez avoir 3 mois d'ancienneté pour vendre.");
        return;
      }

      if (!Number.isFinite(parsedPriceAmount) || parsedPriceAmount <= 0) {
        alert('Veuillez indiquer un prix de vente valide.');
        return;
      }

      if (!currencyCode) {
        alert('Veuillez choisir une devise.');
        return;
      }

      if (paymentMethods.length === 0) {
        alert('Veuillez choisir au moins un moyen de paiement.');
        return;
      }
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('author', author);
    formData.append('author_id', user.id);
    formData.append('user_email', user.email);
    formData.append('description', description);
    formData.append('visibility', visibility);
    formData.append('bookFile', bookFile);
    if (cover) formData.append('cover', cover);
    formData.append('tags', JSON.stringify(tags));
    formData.append('for_sale', String(forSale));
    formData.append('price_amount', forSale ? String(parsedPriceAmount) : '');
    formData.append('currency_code', forSale ? currencyCode : '');
    formData.append('payment_methods', JSON.stringify(forSale ? paymentMethods : []));
    formData.append('platform_fee_rate', String(PLATFORM_FEE_RATE));

    await onUpload(formData);
    onClose();
  };

  const handleBookFileChange = (file) => {
    setBookFile(file || null);
  };

  const handlePayBypass = () => {
    if (!user?.email) {
      alert('Vous devez etre connecte pour payer la derogation.');
      return;
    }
    const returnTo = encodeURIComponent(`${window.location.pathname}?seller_paid=1`);
    navigate(`/payment?type=seller&return_to=${returnTo}`);
  };

  return (
    <div className={asPage ? 'upload-page-root' : 'upload-overlay'}>
      <div className={`upload-modal ${asPage ? 'upload-modal-page' : ''}`}>
        <div className="upload-header">
          <h2>Publier un livre</h2>
          <button className="close-btn" onClick={onClose}>
            {asPage ? 'Retour' : 'X'}
          </button>
        </div>

        <form className="upload-content" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Titre du livre *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            type="text"
            placeholder="Auteur *"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
          />
          <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="upload-row">
            <div className="pdf-section">
              <label className="file-label">
                Selectionner le fichier PDF *
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleBookFileChange(e.target.files[0])}
                  style={{ display: 'block', marginTop: '5px' }}
                />
              </label>
            </div>

            <div className="cover-section">
              {cover ? (
                <img src={URL.createObjectURL(cover)} alt="Couverture" />
              ) : (
                <div className="cover-placeholder">Aucune couverture</div>
              )}
              <label className="file-label">
                Selectionner la couverture (optionnel)
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCover(e.target.files[0])}
                  style={{ display: 'block', marginTop: '5px' }}
                />
              </label>
              {!cover && <small className="cover-auto-note">Si aucune image n est choisie, la couverture par defaut sera utilisee.</small>}
            </div>
          </div>

          <div className="visibility-section">
            <label>
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={(e) => setVisibility(e.target.value)}
              /> Publique
            </label>
            <label>
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={(e) => setVisibility(e.target.value)}
              /> Privee
            </label>
          </div>

          <div className="sale-section">
            <label className="sale-toggle">
              <input
                type="checkbox"
                checked={forSale}
                disabled={!salesEligibility.eligible}
                onChange={(e) => setForSale(e.target.checked)}
              />
              Livre a vendre
            </label>

            {salesEligibility.loading && <small>Verification d'eligibilite en cours...</small>}
            {!salesEligibility.loading && !salesEligibility.eligible && (
              <>
                <small className="sale-warning">
                  {salesEligibility.reason || "La vente est disponible apres 3 mois sur la plateforme."}
                </small>
                <button type="button" className="bypass-btn" onClick={handlePayBypass}>
                  Payer {salesEligibility.bypass_price_usd || 5} USD pour activer la vente
                </button>
              </>
            )}
            {!salesEligibility.loading && salesEligibility.eligible && (
              <small className="sale-ok">Vous etes eligible a la vente.</small>
            )}

            {forSale && (
              <div className="sale-options">
                <div className="row">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Prix du livre"
                    value={priceAmount}
                    onChange={(e) => setPriceAmount(e.target.value)}
                  />
                  <select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)}>
                    {allCurrencies.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                </div>

                <div className="payment-methods">
                  <p>Moyens de paiement acceptes</p>
                  <div className="payment-methods-grid">
                    {availablePaymentMethods.map((method) => (
                      <label key={method} className="payment-method-item">
                        <input
                          type="checkbox"
                          checked={paymentMethods.includes(method)}
                          onChange={() => handleTogglePaymentMethod(method)}
                        />
                        {method}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="commission-info">
                  <small>
                    Commission plateforme: 1% | Frais estimes: {Number.isFinite(estimatedFee) ? estimatedFee.toFixed(2) : '0.00'} {currencyCode}
                    {' '}| Montant vendeur estime: {Number.isFinite(estimatedSellerAmount) ? estimatedSellerAmount.toFixed(2) : '0.00'} {currencyCode}
                  </small>
                </div>
              </div>
            )}
          </div>

          <div className="tags-section">
            <div className="row">
              <input
                type="text"
                placeholder="Ajouter un tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
              />
              <button type="button" onClick={handleAddTag}>Ajouter</button>
            </div>
            <div className="tag-list">
              {tags.map((tag) => (
                <div key={tag} className="tag">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)}>X</button>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" className="publish-btn">Publier</button>
        </form>
      </div>
    </div>
  );
}

export default UploadModal;

